import {
	formatPhoneAsTyped,
	phoneBackspaceOverFormatting,
	phoneCaretAfterDigits,
	phoneDigitsBefore,
} from '@supply-mover-2-0/core';
import type React from 'react';
import { useLayoutEffect, useRef } from 'react';
import { Input } from './Input';

type NativeInputProps = Omit<
	React.InputHTMLAttributes<HTMLInputElement>,
	'value' | 'onChange' | 'type' | 'inputMode' | 'size'
>;

interface TypedPhoneInputProps extends NativeInputProps {
	/** The number as stored: typed text, already formatted, or E.164. */
	value: string;
	/** The formatted text, or null when the box is emptied. */
	onChange: (value: string | null) => void;
}

/**
 * PD-17590 — a phone box that writes the number as "(248) 555-1234" while it is typed
 * (formatPhoneAsTyped). The text keeps an extension and any other country as typed, and
 * the server stores it in E.164 as before. The caret stays after the digit just typed,
 * and Backspace right after a ")" or "-" removes the digit before it rather than nothing.
 */
export function TypedPhoneInput({
	value,
	onChange,
	...rest
}: TypedPhoneInputProps): React.ReactElement {
	const ref = useRef<HTMLInputElement>(null);
	const caret = useRef<number | null>(null);
	const shown = formatPhoneAsTyped(value);

	useLayoutEffect(() => {
		const input = ref.current;
		const target = caret.current;
		caret.current = null;
		if (input != null && target != null && document.activeElement === input) {
			input.setSelectionRange(target, target);
		}
	});

	return (
		<Input
			{...rest}
			ref={ref}
			type="tel"
			inputMode="tel"
			value={shown}
			onChange={(event) => {
				let text = event.target.value;
				let position = event.target.selectionStart ?? text.length;
				const inputType = (event.nativeEvent as Partial<InputEvent>).inputType;
				if (inputType === 'deleteContentBackward') {
					const fixed = phoneBackspaceOverFormatting(shown, text, position);
					if (fixed != null) {
						text = fixed.text;
						position = fixed.caret;
					}
				}
				const formatted = formatPhoneAsTyped(text);
				caret.current =
					position >= text.length
						? formatted.length
						: formatted === text
							? position
							: phoneCaretAfterDigits(
									formatted,
									phoneDigitsBefore(text, position),
								);
				onChange(formatted === '' ? null : formatted);
			}}
		/>
	);
}
